import { ShieldCheck, Hammer, Heart, Users } from "lucide-react";
import SEO from "@/components/site/SEO";
import PageHeader from "@/components/site/PageHeader";
import QuoteCTA from "@/components/site/QuoteCTA";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Show up, every time",
    body: "We answer the phone. We arrive on the day we said we would. It shouldn't be a differentiator — but it is.",
  },
  {
    icon: Hammer,
    title: "Do the boring stuff well",
    body: "Prep, masking, and clean lines are 80% of a great striping job. We don't cut those corners.",
  },
  {
    icon: Heart,
    title: "Local accountability",
    body: "We live here. If a line is crooked, we hear about it at the grocery store. So we don't paint crooked lines.",
  },
  {
    icon: Users,
    title: "Respect your business",
    body: "We schedule around your customers, your tenants, and your staff — not around our calendar.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About 901 Lots — Memphis Parking Lot Striping Crew"
        description="901 Lots is a small, local parking lot striping and sealcoating crew based in Memphis. We answer the phone, we show up on time, and we paint lines we're proud of."
        path="/about"
      />
      <PageHeader
        eyebrow="About"
        title="A Memphis-grown pavement crew."
        subtitle="901 Lots is a small, focused team that stripes and seals parking lots for businesses across the Mid-South. No franchises, no call centers — just a crew, a truck, and a phone that you can actually reach."
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-slate-900 text-3xl sm:text-4xl">
              Built around one idea: a parking lot is the first thing your customer sees.
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
              <p>
                We started 901 Lots because property owners across the
                Mid-South were tired of the same story: a contractor that
                wouldn't return calls, a quote that doubled mid-job, lines
                that faded before the next quarter.
              </p>
              <p>
                Our model is simple. We answer the phone. We walk your
                site. We give you a fixed, line-item quote. And then we
                show up — typically after-hours so your business never
                slows down — and paint lines that hold up.
              </p>
              <p>
                Whether it's a 12-stall church lot in Bartlett or a
                300-space shopping center in Olive Branch, the standard
                is the same: pavement you're proud to drive on.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <img
                src="/img/striping-fresh.jpg"
                alt="Freshly striped parking lot"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-blue-700">
            How we work
          </div>
          <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl">
            Four things we promise.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div
                key={v.title}
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white border border-slate-200 p-6 rounded-lg"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-700">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-display font-semibold text-slate-900 text-lg">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
